import {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import merge from 'lodash/merge';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';

const getStorageValue = <T extends Record<string, unknown>>(key: string, defaultValue: T) => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(key);

    if (saved === null) return defaultValue;

    const parsed = isObject(JSON.parse(saved || '{}'))
      ? merge(defaultValue, JSON.parse(saved))
      : JSON.parse(saved);

    if (!parsed?.expiry || parsed?.expiry < new Date().getTime()) {
      localStorage.removeItem(key);
      return defaultValue;
    }

    return omit(parsed, 'expiry');
  }

  return defaultValue;
};

export const useLocalStorage = <T extends Record<string, unknown>>
  (key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => getStorageValue<T>(key, defaultValue));

  useEffect(() => {
    const now = new Date();
    const inTwoMonths = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());

    localStorage.setItem(key, JSON.stringify({
      ...value,
      expiry: inTwoMonths.getTime(),
    }));
  }, [key, value]);

  return [value, setValue];
};
